import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useAtom, useAtomValue } from "jotai";
import { useTranslation } from "react-i18next";
import { tasksAtom } from "../../../../../atoms/v0/taskAtoms";
import {
  userActiveTrackingAtom,
  startTaskTrackingAtom,
  stopTaskTrackingAtom
} from "../../../../../atoms/v3/focusAtoms";
import ClockAreaFooter from "./ClockAreaFooter";
import ClockModeIcon from "../ClockModeIcon";
import { TimerAlarmModal } from "./TimerAlarmModal";

export interface TaskTimerAreaState {
  isRunning: boolean;
  elapsed: number;
  startTime: string | null; // ISO 字符串
  justFinished: boolean;
}

interface TaskTimerAreaProps {
  taskId?: string;
  currentTime: Date;
  timerDuration?: number;
  timerDurationSeconds?: number; // 可选的秒数配置
  onEditArea?: () => void; // 可选的编辑区域回调
  onSaveState?: (state: TaskTimerAreaState) => void; // 新增：保存状态事件
  initialState?: TaskTimerAreaState; // 新增：初始状态
  soundNotification?: boolean; // 铃声提醒开关
  vibrationNotification?: boolean; // 震动提醒开关
  soundFile?: 'vivora' | 'delay' | 'sms'; // 音频文件选择
}

/**
 * 任务计时器组件 - 独立倒计时器
 */
export const TaskTimerArea: React.FC<TaskTimerAreaProps> = ({
  taskId,
  currentTime,
  timerDuration = 45,
  timerDurationSeconds = 0,
  onEditArea = () => {},
  onSaveState = () => {}, // 默认空函数
  initialState,
  soundNotification = false,
  vibrationNotification = false,
  soundFile = 'vivora',
}) => {
  const { t } = useTranslation(["v3/clock"]);
  const tasks = useAtomValue(tasksAtom);

  // Focus状态和追踪原子
  const userActiveTracking = useAtomValue(userActiveTrackingAtom);
  const [, startTaskTracking] = useAtom(startTaskTrackingAtom);
  const [, stopTaskTracking] = useAtom(stopTaskTrackingAtom);

  // 独立计时器状态
  const [isRunning, setIsRunning] = useState(initialState?.isRunning ?? false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [justFinished, setJustFinished] = useState(initialState?.justFinished ?? false);
  const [showAlarmModal, setShowAlarmModal] = useState(false);

  // 用 ref 持久化 elapsed 和 startTime，避免 interval useEffect 频繁重建
  const elapsedRef = useRef(initialState?.elapsed ?? 0);
  const startTimeRef = useRef<Date | null>(
    initialState?.startTime ? new Date(initialState.startTime) : null
  );

  // 兼容外部显示
  const [elapsed, setElapsed] = useState(initialState?.elapsed ?? 0);
  const [startTime, setStartTime] = useState<Date | null>(
    initialState?.startTime ? new Date(initialState.startTime) : null
  );

  const task = taskId ? tasks.find((t) => t.id === taskId) : null;
  const totalSeconds = Math.floor(Math.floor(timerDuration) * 60) + timerDurationSeconds;

  // 初始化时恢复 timeLeft
  useEffect(() => {
    if (initialState) {
      let left = totalSeconds;
      if (initialState.isRunning && initialState.startTime) {
        const now = new Date();
        const diff = Math.floor((now.getTime() - new Date(initialState.startTime).getTime()) / 1000);
        left = totalSeconds - (initialState.elapsed + diff);
      } else {
        left = totalSeconds - (initialState.elapsed ?? 0);
      }
      setTimeLeft(left > 0 ? left : 0);
    }
  }, []);

  // 计时器逻辑（基于 DateTime），只依赖 isRunning
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isRunning) {
      interval = setInterval(() => {
        if (startTimeRef.current) {
          const now = new Date();
          const diff = Math.floor((now.getTime() - startTimeRef.current.getTime()) / 1000);
          const totalElapsed = elapsedRef.current + diff;
          const left = totalSeconds - totalElapsed;
          if (left <= 0) {
            setIsRunning(false);
            setJustFinished(true);
            setTimeLeft(0);

            // 显示闹钟模态框
            setShowAlarmModal(true);

            // 如果有关联任务，停止云端追踪
            if (taskId) {
              stopTaskTracking(taskId);
            }

            // 结束时保存
            onSaveState({
              isRunning: false,
              elapsed: totalSeconds,
              startTime: null,
              justFinished: true,
            });
          } else {
            setTimeLeft(left);
          }
        }
      }, 1000);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isRunning]);

  // 每5秒自动保存一次状态
  useEffect(() => {
    if (!isRunning) return;
    const interval = setInterval(() => {
      onSaveState({
        isRunning,
        elapsed: elapsedRef.current,
        startTime: startTimeRef.current ? startTimeRef.current.toISOString() : null,
        justFinished,
      });
    }, 5000);
    return () => clearInterval(interval);
  }, [isRunning, justFinished, onSaveState]);

  // 使用ref来保存最新的状态，用于组件卸载时的清理
  const isRunningRef = useRef(isRunning);
  const taskIdRef = useRef(taskId);

  // 更新ref的值
  useEffect(() => {
    isRunningRef.current = isRunning;
  }, [isRunning]);

  useEffect(() => {
    taskIdRef.current = taskId;
  }, [taskId]);

  // 组件卸载时清理：如果计时器正在运行，停止云端追踪
  useEffect(() => {
    return () => {
      // 组件卸载时，如果计时器正在运行且有关联任务，停止云端追踪
      if (isRunningRef.current && taskIdRef.current) {
        stopTaskTracking(taskIdRef.current);
      }
    };
  }, [stopTaskTracking]); // 只依赖stopTaskTracking函数

  // 页面关闭/刷新时清理：如果计时器正在运行，停止云端追踪
  useEffect(() => {
    const handleBeforeUnload = () => {
      // 页面关闭时，如果计时器正在运行且有关联任务，停止云端追踪
      if (isRunningRef.current && taskIdRef.current) {
        // 使用同步方式发送请求，确保在页面关闭前完成
        navigator.sendBeacon('/api/time-tracking/stop', JSON.stringify({
          taskId: taskIdRef.current
        }));
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []); // 空依赖数组，只在组件挂载/卸载时执行

  // 操作时保存状态
  const saveState = (next?: Partial<TaskTimerAreaState>) => {
    onSaveState({
      isRunning: next?.isRunning ?? isRunning,
      elapsed: next?.elapsed ?? elapsedRef.current,
      startTime: (next?.startTime !== undefined ? next.startTime : (startTimeRef.current ? startTimeRef.current.toISOString() : null)),
      justFinished: next?.justFinished ?? justFinished,
    });
  };

  // 开始计时器
  const startTimer = async () => {
    elapsedRef.current = 0;
    setElapsed(0);
    setTimeLeft(totalSeconds);
    const now = new Date();
    startTimeRef.current = now;
    setStartTime(now);
    setIsRunning(true);
    setJustFinished(false);
    saveState({ isRunning: true, elapsed: 0, startTime: now.toISOString(), justFinished: false });

    // 如果有关联任务，开始云端追踪
    if (taskId) {
      const durationMs = totalSeconds * 1000; // 转换为毫秒
      startTaskTracking({ taskId, durationMs });
    }
  };

  // 暂停/继续计时器
  const toggleTimer = () => {
    if (isRunning) {
      // 暂停：累计已用时长
      if (startTimeRef.current) {
        const now = new Date();
        const diff = Math.floor((now.getTime() - startTimeRef.current.getTime()) / 1000);
        const newElapsed = elapsedRef.current + diff;
        elapsedRef.current = newElapsed;
        setElapsed(newElapsed);
        setIsRunning(false);
        startTimeRef.current = null;
        setStartTime(null);
        saveState({ isRunning: false, elapsed: newElapsed, startTime: null });
      } else {
        setIsRunning(false);
        saveState({ isRunning: false });
      }

      // 暂停时停止云端追踪
      if (taskId) {
        stopTaskTracking(taskId);
      }
    } else {
      // 继续：重设 startTime
      const now = new Date();
      startTimeRef.current = now;
      setStartTime(now);
      setIsRunning(true);
      saveState({ isRunning: true, startTime: now.toISOString() });

      // 继续时重新开始云端追踪
      if (taskId) {
        const remainingMs = timeLeft * 1000; // 剩余时间转换为毫秒
        startTaskTracking({ taskId, durationMs: remainingMs });
      }
    }
  };

  // 停止计时器（不重置时间）
  const stopTimer = () => {
    // 如果正在运行且有关联任务，停止云端追踪
    if (isRunning && taskId) {
      stopTaskTracking(taskId);
    }

    setIsRunning(false);
    startTimeRef.current = null;
    setStartTime(null);
    saveState({ isRunning: false, startTime: null });
  };

  // 重置计时器
  const resetTimer = () => {
    // 如果正在运行且有关联任务，停止云端追踪
    if (isRunning && taskId) {
      stopTaskTracking(taskId);
    }

    setIsRunning(false);
    setTimeLeft(0);
    startTimeRef.current = null;
    setStartTime(null);
    elapsedRef.current = 0;
    setElapsed(0);
    setJustFinished(false);
    saveState({ isRunning: false, elapsed: 0, startTime: null, justFinished: false });
  };

  // 格式化时间显示
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // 计算进度百分比
  const progress =
    timeLeft > 0 ? ((totalSeconds - timeLeft) / totalSeconds) * 100 : 0;

  return (
    <>
    <div className="h-full w-full flex flex-col items-center justify-center">
      <div className="grow h-1 flex items-center w-full px-[18px]">
        <div className="flex flex-col w-1 grow">
          {/* 进度条 */}
          <div className="w-[72px] bg-[var(--cm-comp-bg-secondary)] rounded-full h-[2px] mb-[6px]">
            <div
              className="bg-[var(--cm-comp-text-color)] h-[2px] rounded-full transition-all duration-1000"
              style={{ width: `${justFinished ? 100 : progress}%` }}
            />
          </div>

          {/* 计时器显示 */}
          <div className="text-[2em] clock-digit-font font-bold">
            {justFinished
              ? "FIN"
              : timeLeft > 0
                ? formatTime(timeLeft)
                : Math.floor(timerDuration) + ":" + (timerDurationSeconds || 0).toString().padStart(2, "0")}
          </div>

          <div className="flex gap-[6px] items-center opacity-45">
            {/* 开始时间 */}
            {/* {startTime && (
              <>
                <div className="text-[10px]">
                  {t("v3/clock:timer.startedAt", "开始于")}:{" "}
                  {startTime.toLocaleTimeString()}
                </div>

                <div className="h-[8px] w-[1px] bg-[var(--cm-comp-text-color)] opacity-20"></div>
              </>
            )} */}

            {task ? (
              <div className="text-[10px] truncate">{task.title}</div>
            ) : (
              <div className="text-[10px] truncate">
                {t("v3/clock:components.taskTimer.pureTimer", "纯计时器模式")}
              </div>
            )}
          </div>
        </div>

        {/* 控制按钮 - Minimal 风格 */}
        <div className="flex items-center gap-[6px]">
          <button
            className="circle-button"
            onClick={isRunning ? stopTimer : resetTimer}
            disabled={!isRunning && timeLeft <= 0}
            title={isRunning ? "停止计时器" : "重置计时器"}
          >
            <ClockModeIcon type="stop" size={14} />
          </button>
          <button
            className="circle-button circle-button-big"
            onClick={timeLeft > 0 ? toggleTimer : startTimer}
          >
            <ClockModeIcon type={isRunning ? "pause" : "play"} size={18} />
          </button>
          <button className="circle-button" onClick={onEditArea}>
            <ClockModeIcon type="dots" size={14} />
          </button>
        </div>
      </div>

      <ClockAreaFooter text="计时器" icon={"⏱️"} onClick={onEditArea} />
    </div>

    {/* 计时器闹钟模态框 - 使用 Portal 渲染到 body */}
    {showAlarmModal && createPortal(
      <TimerAlarmModal
        isOpen={showAlarmModal}
        onClose={() => setShowAlarmModal(false)}
        taskId={taskId}
        timerDuration={timerDuration}
        timerDurationSeconds={timerDurationSeconds}
        soundFile={soundFile}
        enableSound={soundNotification}
        enableVibration={vibrationNotification}
      />,
      document.body
    )}
  </>
  );
};

export default TaskTimerArea;
